import { Add } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { SolaForceDataGrid } from '~/components/SolaForceDataGrid';
import { useDataGridTreeRowGrouping } from '~/components/SolaForceDataGrid/hooks/useDataGridTreeRowGrouping';
import { SolaForceHeading } from '~/components/SolaForceHeading';
import { useDialogState } from '~/hooks/useDialogState';
import { AddWorklistDialog } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/AddWorklistDialog';
import { WorkListTableRow } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/data';
import { EditWorklistDialog } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistDialog';
import { EditWorklistTaskDialog } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/EditWorklistTaskDialog';
import { useWorklistTable } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/useWorklistTable';
import { useWorklistTableColumns } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/useWorklistTableColumns';
import { useWorklistTableTaskEditing } from '~/scenes/PeopleModule/components/Tabs/CollaborationTab/WorklistTable/useWorklistTableTaskEditing';
import { LoadingView } from '~/scenes/PortalViews/components/LoadingView';

import { useStyles } from './useStyles';

export const WorklistTable = () => {
  const { t } = useTranslation();

  const sxStyles = useStyles();

  const {
    rows,
    onUpdate,
    employeeId,
    workListOptions,
    canAddWorkList,
    isLoading,
    getNewTaskFormData,
  } = useWorklistTable();

  const {
    worklistEditing: {
      isEditWorklistDialogOpen,
      selectedWorklist,
      onEditWorklist,
      onCloseWorklistEdit,
    },
    taskEditing: {
      isEditTaskDialogOpen,
      selectedTask,
      onEditTask,
      onAddTask,
      onCloseTaskEdit,
    },
  } = useWorklistTableTaskEditing();

  const dataGridColumnProps = useWorklistTableColumns({
    onUpdate,
    onEditTask,
    onAddTask,
    getNewTaskFormData,
    onEditWorklist,
  });

  const apiRef = useGridApiRef();

  const { dataGridProps: dataGridTreeProps } =
    useDataGridTreeRowGrouping<WorkListTableRow>({
      dataGridProps: {
        apiRef,
        rows,
        getRowId: (row) => row.id,
        getTreeDataPath: (row) => row.path,
        ...dataGridColumnProps,
      },
    });

  const newWorklistDialog = useDialogState();

  if (isLoading) {
    return <LoadingView />;
  }

  return (
    <>
      {canAddWorkList && (
        <SolaForceHeading heading={' '}>
          <Button onClick={newWorklistDialog.open} startIcon={<Add />}>
            {t('laAdd')}
          </Button>
        </SolaForceHeading>
      )}
      <Box sx={sxStyles.container}>
        <SolaForceDataGrid
          {...dataGridTreeProps}
          experimentalFeatures={{ columnGrouping: true }}
          hideFooter={true}
          density={'compact'}
          loading={isLoading}
          autoHeight={true}
          getRowClassName={({ row: { rowType } }) => {
            return `row-type-${rowType}`;
          }}
          sx={sxStyles.dataGrid}
          getRowHeight={() => 'auto'}
        />
      </Box>
      {newWorklistDialog.isOpen && (
        <AddWorklistDialog
          open={newWorklistDialog.isOpen}
          onClose={newWorklistDialog.close}
          onUpdate={onUpdate}
          employeeId={employeeId}
          workListOptions={workListOptions}
          onTaskListCreate={onUpdate}
        />
      )}
      {isEditWorklistDialogOpen && !!selectedWorklist && (
        <EditWorklistDialog
          open={isEditWorklistDialogOpen}
          selectedWorklist={selectedWorklist}
          worklistTemplates={workListOptions}
          onClose={onCloseWorklistEdit}
          employeeId={employeeId}
          onUpdate={onUpdate}
        />
      )}
      {isEditTaskDialogOpen && (
        <EditWorklistTaskDialog
          open={isEditTaskDialogOpen}
          selectedTask={selectedTask}
          onClose={onCloseTaskEdit}
          employeeId={employeeId}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};
